/**
 * PHRASE BLOOM の音楽理論ユーティリティ。
 * スケール定義・終止形（カデンツ）のターゲット音・MIDI ノート変換をまとめる。
 */

/** ピッチクラス 0..11 に対応する音名（シャープ表記） */
export const NOTE_NAMES = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
] as const;

export type ScaleId =
  | "major"
  | "naturalMinor"
  | "harmonicMinor"
  | "melodicMinor"
  | "majorPentatonic"
  | "minorPentatonic"
  | "bluesMinor"
  | "bluesMajor"
  | "dorian"
  | "phrygian"
  | "lydian"
  | "mixolydian"
  | "lydianDominant"
  | "altered"
  | "wholeTone"
  | "dimHalfWhole"
  | "dimWholeHalf"
  | "bebopDominant"
  | "bebopMajor"
  | "phrygianDominant"
  | "hungarianMinor"
  | "hirajoshi"
  | "insen"
  | "ryukyu"
  | "sus2"
  | "sus4"
  | "sevenSus4"
  | "nineSus4"
  | "add9"
  | "minorAdd9"
  | "major7"
  | "minor7"
  | "dominant7"
  | "major9"
  | "minor9"
  | "dominant9"
  | "sixNine";

export interface ScaleDef {
  id: ScaleId;
  /** パネル刻印用の短い表記 */
  label: string;
  /** ルートからの半音オフセット（1 オクターブ内） */
  intervals: number[];
}

/**
 * 並びは 基本スケール → ペンタ/ブルース → 教会旋法 → ジャズ/シンメトリック →
 * エキゾチック/和風 → コード系ノートコレクション（sus / 7th / 9th の
 * アルペジオ素材）。9th などのテンションは 1 オクターブ内のピッチクラスに
 * 畳んで定義する。
 */
export const SCALES: ScaleDef[] = [
  // ---- 基本スケール ----
  { id: "major", label: "MAJOR", intervals: [0, 2, 4, 5, 7, 9, 11] },
  { id: "naturalMinor", label: "MINOR", intervals: [0, 2, 3, 5, 7, 8, 10] },
  { id: "harmonicMinor", label: "HARM MIN", intervals: [0, 2, 3, 5, 7, 8, 11] },
  { id: "melodicMinor", label: "MEL MIN", intervals: [0, 2, 3, 5, 7, 9, 11] },
  // ---- ペンタトニック / ブルース ----
  { id: "majorPentatonic", label: "MAJ PENTA", intervals: [0, 2, 4, 7, 9] },
  { id: "minorPentatonic", label: "MIN PENTA", intervals: [0, 3, 5, 7, 10] },
  { id: "bluesMinor", label: "BLUES", intervals: [0, 3, 5, 6, 7, 10] },
  { id: "bluesMajor", label: "MAJ BLUES", intervals: [0, 2, 3, 4, 7, 9] },
  // ---- 教会旋法 ----
  { id: "dorian", label: "DORIAN", intervals: [0, 2, 3, 5, 7, 9, 10] },
  { id: "phrygian", label: "PHRYGIAN", intervals: [0, 1, 3, 5, 7, 8, 10] },
  { id: "lydian", label: "LYDIAN", intervals: [0, 2, 4, 6, 7, 9, 11] },
  { id: "mixolydian", label: "MIXO", intervals: [0, 2, 4, 5, 7, 9, 10] },
  // ---- ジャズ / シンメトリック ----
  { id: "lydianDominant", label: "LYD DOM", intervals: [0, 2, 4, 6, 7, 9, 10] },
  { id: "altered", label: "ALTERED", intervals: [0, 1, 3, 4, 6, 8, 10] },
  { id: "wholeTone", label: "WHOLE TONE", intervals: [0, 2, 4, 6, 8, 10] },
  { id: "dimHalfWhole", label: "DIM H-W", intervals: [0, 1, 3, 4, 6, 7, 9, 10] },
  { id: "dimWholeHalf", label: "DIM W-H", intervals: [0, 2, 3, 5, 6, 8, 9, 11] },
  { id: "bebopDominant", label: "BEBOP DOM", intervals: [0, 2, 4, 5, 7, 9, 10, 11] },
  { id: "bebopMajor", label: "BEBOP MAJ", intervals: [0, 2, 4, 5, 7, 8, 9, 11] },
  // ---- エキゾチック / 和風 ----
  { id: "phrygianDominant", label: "PHRYG DOM", intervals: [0, 1, 4, 5, 7, 8, 10] },
  { id: "hungarianMinor", label: "HUNG MIN", intervals: [0, 2, 3, 6, 7, 8, 11] },
  { id: "hirajoshi", label: "HIRAJOSHI", intervals: [0, 2, 3, 7, 8] },
  { id: "insen", label: "INSEN", intervals: [0, 1, 5, 7, 10] },
  { id: "ryukyu", label: "RYUKYU", intervals: [0, 4, 5, 7, 11] },
  // ---- コード系ノートコレクション ----
  { id: "sus2", label: "SUS2", intervals: [0, 2, 7] },
  { id: "sus4", label: "SUS4", intervals: [0, 5, 7] },
  { id: "sevenSus4", label: "7SUS4", intervals: [0, 5, 7, 10] },
  { id: "nineSus4", label: "9SUS4", intervals: [0, 2, 5, 7, 10] },
  { id: "add9", label: "ADD9", intervals: [0, 2, 4, 7] },
  { id: "minorAdd9", label: "mADD9", intervals: [0, 2, 3, 7] },
  { id: "major7", label: "MAJ7", intervals: [0, 4, 7, 11] },
  { id: "minor7", label: "MIN7", intervals: [0, 3, 7, 10] },
  { id: "dominant7", label: "DOM7", intervals: [0, 4, 7, 10] },
  { id: "major9", label: "MAJ9", intervals: [0, 2, 4, 7, 11] },
  { id: "minor9", label: "MIN9", intervals: [0, 2, 3, 7, 10] },
  { id: "dominant9", label: "DOM9", intervals: [0, 2, 4, 7, 10] },
  { id: "sixNine", label: "6/9", intervals: [0, 2, 4, 7, 9] },
];

export const getScaleById = (id: ScaleId): ScaleDef =>
  SCALES.find((s) => s.id === id) ?? SCALES[0];

/** 終止形（最後の音を寄せる機能和音） */
export type CadenceId = "tonic" | "subdominant" | "dominant";

/** 各機能和音のルート（トニックからの半音距離） */
const CADENCE_ROOTS: Record<CadenceId, number> = {
  tonic: 0,
  subdominant: 5,
  dominant: 7,
};

/**
 * 終止形ターゲットのピッチクラス集合（絶対 pc）を返す。
 *
 * 三和音（根音・三度・五度）を半音公式で組み立て、三度はスケールの性格
 * （短三度系スケールならマイナー）を基本に、スケール内音があればそちらを
 * 優先する。ハーモニックマイナーの V が導音を含んでメジャー化する、
 * といった挙動が自然に得られる。
 */
export const getCadencePitchClasses = (
  scale: ScaleDef,
  cadence: CadenceId,
  rootPc: number,
): number[] => {
  const chordRoot = CADENCE_ROOTS[cadence];
  const pcs = new Set(scale.intervals);
  const minorish = pcs.has(3) && !pcs.has(4);
  let third = minorish ? 3 : 4;
  const altThird = third === 4 ? 3 : 4;
  if (!pcs.has((chordRoot + third) % 12) && pcs.has((chordRoot + altThird) % 12)) {
    third = altThird;
  }
  return [
    (rootPc + chordRoot) % 12,
    (rootPc + chordRoot + third) % 12,
    (rootPc + chordRoot + 7) % 12,
  ];
};

/**
 * ルート pc のスケール構成音を MIDI 0..127 全域で昇順に並べたプールを作る。
 * RANGE 制限は設けず、フレーズは開始地点（KEY + OCTAVE）から MIDI の
 * 上限・下限まで自在に上昇下降できる。
 */
export const buildScalePool = (rootPc: number, scale: ScaleDef): number[] => {
  const pool: number[] = [];
  for (let base = ((rootPc % 12) + 12) % 12; base <= 127; base += 12) {
    for (const interval of scale.intervals) {
      const midi = base + interval;
      if (midi >= 0 && midi <= 127) pool.push(midi);
    }
  }
  return pool;
};

/** KEY + OCTAVE から開始地点の MIDI ノートを求める（C4 = 60 の慣習） */
export const rootMidiOf = (rootPc: number, octave: number): number =>
  (octave + 1) * 12 + rootPc;

/**
 * プール上で degrees 度数ぶん移動した音を返す（下ハモの写像などに使う）。
 * プール範囲外へはクランプする。midi がプールに無い場合は最近傍から移動する。
 */
export const shiftScaleDegrees = (
  pool: number[],
  midi: number,
  degrees: number,
): number => {
  let idx = pool.indexOf(midi);
  if (idx === -1) {
    idx = 0;
    for (let i = 1; i < pool.length; i++) {
      if (Math.abs(pool[i] - midi) < Math.abs(pool[idx] - midi)) idx = i;
    }
  }
  const shifted = Math.min(pool.length - 1, Math.max(0, idx + degrees));
  return pool[shifted];
};

/** MIDI ノート番号 → "C4" 形式の表記 */
export const midiNoteName = (midi: number): string =>
  `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;
